// V17.520 (brief 2026-10-25-004): read-only offline support for the AR field map.
//
// Human ruling: "Read-only cache" — with no signal the officer still sees the
// last-loaded portfolio; edits need signal; nothing is queued.
//
// 1. useOnlineStatus — offline when the browser says so, when a fetch failed, or
//    when the service worker (public/sw.js) had to answer a field-map read from
//    its cache. The worker's notice carries when that copy was fetched, which is
//    the "last updated" time the banner shows. Writes are blocked only on a real
//    loss of signal, not on a slow read (see OnlineStatus.writesBlocked).
// 2. The field-map query cache in localStorage — only the field-map queries,
//    only for the logged-in user (JWT `sub`), at most 3 days old. It is cleared
//    on any user change (logout included), together with the worker's read
//    cache, so a second user on the same phone never sees the first one's
//    portfolio. AuthContext is not touched: the owner is read from the token.
import { useSyncExternalStore } from 'react';
import { dehydrate, hydrate, type QueryClient, type QueryKey } from 'react-query';

// ---------------------------------------------------------------------------
// 1. Online status
// ---------------------------------------------------------------------------

/** Message types posted by public/sw.js. */
export const SW_MSG_SERVED_FROM_CACHE = 'vtw-sw:served-from-cache';
export const SW_MSG_NETWORK_OK = 'vtw-sw:network-ok';

const FIELD_MAP_PATH = '/ar/field-officer/map';

interface NetSnapshot {
  navOnline: boolean;
  swFallback: boolean;
  /** When the copy the worker served from cache was fetched (epoch ms). */
  cachedAt: number | null;
}

const listeners = new Set<() => void>();
let snapshot: NetSnapshot = { navOnline: true, swFallback: false, cachedAt: null };
let detach: (() => void) | null = null;

function setSnapshot(next: Partial<NetSnapshot>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => l());
}

function attach() {
  const onOnline = () => setSnapshot({ navOnline: true, swFallback: false, cachedAt: null });
  const onOffline = () => setSnapshot({ navOnline: false });
  const onSwMessage = (e: Event) => {
    const data = (e as MessageEvent).data as { type?: string; url?: string; cachedAt?: number | null } | null;
    if (!data || typeof data.type !== 'string') return;
    if (data.type === SW_MSG_SERVED_FROM_CACHE) {
      const isMap = typeof data.url === 'string' && data.url.includes(FIELD_MAP_PATH);
      setSnapshot({ swFallback: true, cachedAt: isMap && data.cachedAt ? data.cachedAt : snapshot.cachedAt });
    } else if (data.type === SW_MSG_NETWORK_OK) {
      setSnapshot({ swFallback: false, cachedAt: null });
    }
  };
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
  sw?.addEventListener('message', onSwMessage);
  snapshot = { ...snapshot, navOnline: typeof navigator === 'undefined' ? true : navigator.onLine !== false };
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    sw?.removeEventListener('message', onSwMessage);
  };
}

function subscribe(listener: () => void) {
  if (!detach) detach = attach();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => snapshot;

export interface OnlineStatus {
  /** Show the offline banner: no signal, a failed read, or the worker answered from its cache. */
  offline: boolean;
  /**
   * Block writes: only a real loss of signal (browser offline or a read that got no
   * response). A slow read the worker answered from cache is NOT enough — the
   * phone may well be online, and a write that then fails says so itself.
   */
  writesBlocked: boolean;
  /** When the cached field-map data on screen was fetched, if the worker said so. */
  cachedAt: number | null;
}

/**
 * @param fetchFailed pass true when the page's own read failed with no response
 *   (a network error) — `navigator.onLine` alone stays true on a dead connection.
 */
export function useOnlineStatus(fetchFailed = false): OnlineStatus {
  const s = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const noSignal = !s.navOnline || fetchFailed;
  return { offline: noSignal || s.swFallback, writesBlocked: noSignal, cachedAt: s.cachedAt };
}

/** True when a react-query error is a network failure (no HTTP response at all). */
export function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { response?: unknown; request?: unknown; message?: string };
  return e.response === undefined && (e.request !== undefined || e.message === 'Network Error');
}

// ---------------------------------------------------------------------------
// 2. The per-user field-map query cache
// ---------------------------------------------------------------------------

export const FIELD_MAP_CACHE_KEY = 'vtw.fieldMapCache.v1';
export const FIELD_MAP_CACHE_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
/** Filter combinations kept (newest first) — bounds the localStorage size. */
const MAX_PERSISTED_QUERIES = 3;
const API_CACHE_PREFIX = 'vtw-api-';

interface PersistedRecord {
  owner: string;
  savedAt: number;
  state: ReturnType<typeof dehydrate>;
}

/** The persister key filter: only ['ar', 'field-officer', 'map', …]. */
export function isFieldMapQueryKey(key: QueryKey | unknown): boolean {
  return Array.isArray(key) && key[0] === 'ar' && key[1] === 'field-officer' && key[2] === 'map';
}

/** The logged-in user: the `sub` of the stored JWT (the worker keys its cache the same way). */
export function currentUserId(): string | null {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    return payload?.sub ? String(payload.sub) : null;
  } catch {
    return null;
  }
}

function removeStored() {
  try {
    localStorage.removeItem(FIELD_MAP_CACHE_KEY);
  } catch {
    // storage unavailable — nothing to remove
  }
}

export function persistFieldMapQueries(queryClient: QueryClient, now: number = Date.now()): void {
  const owner = currentUserId();
  if (!owner) return;
  const state = dehydrate(queryClient, {
    shouldDehydrateQuery: (q) => isFieldMapQueryKey(q.queryKey) && q.state.status === 'success',
    shouldDehydrateMutation: () => false,
  });
  state.queries = state.queries
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
    .slice(0, MAX_PERSISTED_QUERIES);
  if (state.queries.length === 0) return;
  const record: PersistedRecord = { owner, savedAt: now, state };
  try {
    localStorage.setItem(FIELD_MAP_CACHE_KEY, JSON.stringify(record));
  } catch {
    removeStored(); // quota: better no offline copy than a stale one
  }
}

/** Restores the logged-in user's field-map queries. Anything else found is deleted. */
export function restoreFieldMapQueries(queryClient: QueryClient, now: number = Date.now()): boolean {
  let record: PersistedRecord | null = null;
  try {
    const raw = localStorage.getItem(FIELD_MAP_CACHE_KEY);
    record = raw ? (JSON.parse(raw) as PersistedRecord) : null;
  } catch {
    record = null;
  }
  if (!record) {
    removeStored();
    return false;
  }
  const owner = currentUserId();
  const fresh = typeof record.savedAt === 'number' && now - record.savedAt <= FIELD_MAP_CACHE_MAX_AGE_MS;
  if (!owner || record.owner !== owner || !fresh || !record.state) {
    removeStored();
    return false;
  }
  hydrate(queryClient, record.state);
  return true;
}

/** Wipes every copy of field-map data: memory, localStorage and the worker's read cache. */
export async function clearFieldMapCache(queryClient: QueryClient): Promise<void> {
  queryClient.removeQueries(['ar', 'field-officer']);
  removeStored();
  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const names = await window.caches.keys();
      await Promise.all(names.filter((n) => n.startsWith(API_CACHE_PREFIX)).map((n) => window.caches.delete(n)));
    } catch {
      // Cache Storage unavailable — the worker keys its entries per user anyway
    }
  }
}

let knownOwner: string | null | undefined;

/**
 * Called on every route change. Logout clears the token and navigates to /login,
 * so a changed (or missing) owner here means the previous user's data must go.
 */
export function checkFieldMapCacheOwner(queryClient: QueryClient): Promise<void> {
  const owner = currentUserId();
  if (knownOwner === undefined) {
    knownOwner = owner;
    return Promise.resolve();
  }
  if (owner === knownOwner) return Promise.resolve();
  knownOwner = owner;
  return clearFieldMapCache(queryClient);
}

/** Saves the field-map queries (throttled) whenever one of them changes. Returns an unsubscribe. */
export function setupFieldMapPersistence(queryClient: QueryClient, throttleMs = 1000): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (!event?.query || !isFieldMapQueryKey(event.query.queryKey)) return;
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      persistFieldMapQueries(queryClient);
    }, throttleMs);
  });
  return () => {
    if (timer) clearTimeout(timer);
    unsubscribe();
  };
}

/** Test-only: forget listeners, status and the known owner. */
export function __resetOnlineStatusForTests(): void {
  detach?.();
  detach = null;
  listeners.clear();
  snapshot = { navOnline: true, swFallback: false, cachedAt: null };
  knownOwner = undefined;
}
